@import "../../global/gx-ide-mixins.scss";

:host {
  display: grid;
  block-size: 100%;
  overflow: auto;
  grid-template-rows: max-content 1fr max-content;
}

.section {
  display: contents;
}

.header {
  &-one-col {
    grid-template-columns: 1fr;
  }

  // KB Name and Location are independent inline-field grids, so each label
  // column defaults to `max-content` and sizes to its own caption. Since
  // "Location" is shorter than "KB Name", their inputs would start at
  // different positions. Pin a shared, fixed-width label column on both so
  // the inputs line up vertically while keeping the same label-to-input gap.
  .field-inline {
    grid-template-columns: 4.5rem 1fr;
  }
}
.location-wrapper {
  display: flex;
  align-items: center;
  gap: var(--spacing-gap-xs);
  min-block-size: var(--control-block-size);
}
.location-custom-wrapper {
  display: flex;
  align-items: center;
  gap: var(--spacing-gap-xs);
  flex-grow: 1;
  min-inline-size: 0;
}
.location-field {
  flex-grow: 1;
  min-inline-size: 0;

  > .input {
    inline-size: 100%;
  }
}

.basic {
  display: grid;
  grid-template:
    "basic-top basic-top" max-content
    "back-end front-end" 1fr;
  grid-template-columns: 1fr 1fr;
}
.basic__header {
  grid-area: basic-top;
  grid-template-columns: 1fr 1fr;
}
.dialog-header__back-end,
.dialog-header__front-end {
  padding-block-start: 16px;
}
.main__back-end {
  border-inline-end: var(--section-common-border);
}
.main__back-end,
.main__front-end {
  grid-auto-rows: max-content;
  padding-block: 12px;
  overflow: auto;
}
// Backend-only callout shown immediately below the front end checkboxes.
// The grid has no row gap, so add a top margin for breathing room.
.main__front-end > .kb-storage-callout {
  margin-block-start: var(--spacing-gap-m);
}
// back end
.section__back-end {
  grid-area: back-end;
  grid-auto-rows: max-content;
}
// front end
.section__front-end {
  grid-area: front-end;
}
.section__back-end,
.section__front-end {
  display: grid;
  grid-template-rows: max-content 1fr;
  overflow: auto;
}

.basic,
.advanced {
  padding-block-start: 12px;
  overflow: auto;
  block-size: 100%;
}

.advanced-loader {
  position: relative;
  display: block;
  inline-size: 100%;
  block-size: 100%;
}

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Knowledge Base Storage card (advanced tab)
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
.kb-storage-card {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-gap-l);
  padding-inline: var(--spacing-padding-xl);
}

.kb-storage-card__header {
  display: flex;
  align-items: center;
  gap: var(--spacing-gap-s);
  padding-block-end: var(--spacing-padding-m);
  border-bottom: 1px solid var(--color-border-surface-on-elevation-1);
}

.kb-storage-card__heading {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-gap-xs);
  min-inline-size: 0;
}

.kb-storage-card__title {
  margin: 0;
  color: var(--color-text-neutral-default);
}

.kb-storage-card__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: var(--spacing-gap-xl);
  row-gap: var(--spacing-gap-l);
}

.kb-storage-column {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-gap-m);
  min-inline-size: 0;
}

.kb-storage-column__title {
  display: flex;
  align-items: center;
  gap: var(--spacing-gap-s);
  color: var(--color-text-neutral-disabled);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.kb-storage-column__title-text {
  white-space: nowrap;
}

.kb-storage-column__title-rule {
  flex: 1;
  block-size: 1px;
  background-color: var(--color-border-surface-on-elevation-1);
}

.field-group--stacked {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-gap-m);
}

.server-name-wrapper {
  display: grid;
  grid-template-columns: 1fr max-content;
  gap: var(--spacing-gap-xs);
  min-inline-size: 0;
}

.kb-storage-credentials {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-gap-m);
}

.kb-storage-callout {
  @include callout;
}

.kb-storage-callout-inner {
  display: flex;
  flex-direction: column;
}

.kb-storage-callout__title {
  display: flex;
  gap: var(--spacing-gap-xs);
}

.kb-storage-callout__description {
  color: var(--color-text-neutral-disabled);
}

// WA / TODO: Add a helper class in Mercury to justify text.
.text-align-center {
  text-align: center; // should be resolved on mercury
}

.tab::part(page) {
  // WA / TODO: Remove this line when the ch-tab renders supports scrollbar
  // custom styling
  scrollbar-width: thin;
}

// Align the tab list with the header / footer horizontal padding
// (matches `spacing-body-inline` -> --spacing-padding-xl).
.tab::part(tab-list block-start) {
  padding-inline: var(--spacing-padding-xl);
}

// WA: Staring on Mercury V1.0.0. the checkbox displays an outline all around.
// This requires the checkbox inline-size to be set to max-content.
// TODO: Remove this once the checkbox displays correctly on a later version of Mercury.
.checkbox {
  inline-size: max-content;
}

.footer.dialog-footer-with-border {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-gap-m);

  > .buttons-spacer {
    margin-inline-start: auto;
  }
}

.connection-status {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-gap-xs);

  &__spinner {
    display: inline-block;
    flex-shrink: 0;

    @include spinner(
      var(--icon-s),
      var(--color-accent-primary-default),
      var(--color-accent-primary-pressed)
    );
  }

  &--connecting {
    color: var(--color-accent-primary-on-default);
  }

  &--success {
    color: var(--color-text-success-default);
  }

  &--failed {
    color: var(--color-text-error-default);
  }
}
